import React, { useMemo } from 'react'
import Group from 'components/utils/Group'
import Pill from 'components/Luxkit/Pill'
import LineClockDollarIcon from 'components/Luxkit/Icons/line/LineClockDollarIcon'
import SecureWithDepositTagAndTooltip from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndTooltip'
import Caption from 'components/Luxkit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import { CruiseOfferPriceVariation } from 'lib/cruises/cruiseUtils'
import CruiseDiscountTermsAndConditions from 'components/Cruises/CruiseDiscountTermsAndConditions'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'

interface Props {
  isFlashOffer: boolean;
  disableDeposits: boolean;
  promotionDetails: App.CruisePromotionDetails | null;
  cabinCategory?: string;
  lowestPrice?: CruiseOfferPriceVariation;
  discountPills: App.CruiseDiscountPillDetails | null;
  isStandalone?: boolean;
}

function CruiseTilePrices({
  disableDeposits,
  promotionDetails,
  cabinCategory,
  lowestPrice,
  discountPills,
  isStandalone,
}: Props) {
  const deposit = useMemo(() => (
    <>
      {promotionDetails?.deposit && (
        <Pill
            kind="tertiary"
            variant="primary"
            startIcon={<LineClockDollarIcon />}
          >
          Secure with a {promotionDetails.deposit.formattedValue} deposit
        </Pill>
      )}
      {!promotionDetails?.deposit && (
        <SecureWithDepositTagAndTooltip offerType="cruise" />
      )}
    </>
  )
  , [promotionDetails])

  return (
    <Group gap={8} direction="vertical" fullWidth>
      {isStandalone && !disableDeposits && deposit}
      {!isStandalone && !disableDeposits &&
        <CSSBreakpoint min="desktop">
          {deposit}
        </CSSBreakpoint>
      }
      <Group
        fullWidth
        gap={4}
        direction="horizontal"
        tabletDirection="vertical"
        horizontalAlign="space-between"
        tabletHorizontalAlign="start"
        verticalAlign="end"
      >
        {isStandalone && <div>
          {cabinCategory && (
            <PriceRowPriceCaption>
              {cabinCategory} from
            </PriceRowPriceCaption>
          )}
          {lowestPrice && (
            <PriceRowPrice
              size="L"
              price={lowestPrice.primaryPrice.total}
              saleUnit={lowestPrice.primaryPrice.type}
              taxesAndFees="inclusive"
            />
          )}
          {!lowestPrice?.secondaryPrice &&
            discountPills?.price &&
            discountPills.discountPercentage && (
              <Group direction="horizontal" gap={2} verticalAlign="center">
                <Caption variant="medium" weight="bold" colour="neutral-one">
                  Valued up to <FormatCurrency value={discountPills.price} />
                </Caption>
                <Pill kind="primary" variant="success">
                  -{discountPills.discountPercentage}%
                </Pill>
                <CruiseDiscountTermsAndConditions />
              </Group>
          )}
          {lowestPrice?.secondaryPrice && (
            <>
              <Caption variant="large" colour="neutral-three" as="spam">
                <FormatCurrency
                  value={lowestPrice.secondaryPrice.total}
                />
              </Caption>
              <Caption variant="large" colour="neutral-three" as="spam">
                /{lowestPrice.secondaryPrice.type} · Twin Share
              </Caption>
              {discountPills?.price && discountPills.discountPercentage && (
                <Group direction="horizontal" gap={2} verticalAlign="center">
                  <Caption variant="medium" weight="bold" colour="neutral-one">
                    Valued up to <FormatCurrency value={discountPills.price} />
                  </Caption>
                  <Pill kind="primary" variant="success">
                    -{discountPills.discountPercentage}%
                  </Pill>
                  <CruiseDiscountTermsAndConditions />
                </Group>
              )}
            </>
          )}
          <Caption variant="medium" colour="neutral-three">
            Includes taxes & fees
          </Caption>
          {!lowestPrice?.secondaryPrice && (
            <Caption variant="medium" colour="neutral-three">
              {' '}
              Twin Share{' '}
            </Caption>
          )}
        </div>}
      </Group>
    </Group>
  )
}

export default CruiseTilePrices
